/**
 * Nível de EXIBIÇÃO de um instrumento — GUPM-228.
 * TS puro (regra de arquitetura: sem React, sem Supabase, sem DOM).
 *
 * O app exibia instrumento sem nenhuma leitura como NORMAL verde, por causa de
 * um `?? 'NORMAL'` repetido em 7 pontos de render — inclusive no pino do mapa.
 * Numa tela de segurança de barragem verde afirma "medido e dentro da faixa", e
 * ausência de medição não é isso.
 *
 * POR QUE UM TIPO NOVO, e não um 5º valor em `TarpLevel`: `TarpLevel` é tipo de
 * domínio do TARP — alimenta as máquinas de estado das 3 Vias, o semáforo ANM e
 * a cadeia de notificação, onde "sem leitura" não significa nada. Além disso
 * `MpaActionList` usa `nivel in LEVEL_UI` como guard de narrowing: incluir o
 * estado novo em `LEVEL_UI` faria o guard aceitá-lo e castá-lo para `TarpLevel`.
 * Este estado é de apresentação e fica acima do domínio de emergência.
 */
import type { Canal, TarpLevel } from '../types';
import { foraDeServico } from './fora-de-servico';

/** Instrumento cadastrado para o qual não existe nenhuma leitura. */
export const SEM_LEITURA = 'SEM_LEITURA';

/**
 * Instrumento que MEDE e não tem faixa de controle — GUPM-247.
 *
 * Fato oposto ao `SEM_LEITURA`, e por isso estado separado: aqui a medição
 * existe (os 43 nessa situação concentram 1,41 M das 1,6 M de leituras do
 * banco), o que falta é a régua. A ação do responsável também é outra —
 * cobrar a carta da Galvani, não checar telemetria.
 */
export const SEM_FAIXA = 'SEM_FAIXA';

/**
 * Instrumento que MEDIU antes e está mudo agora — GUPM-303.
 *
 * Terceiro fato distinto dos dois acima: aqui existe medição E existe régua, o que falta
 * é dado ATUAL. Sem este estado o instrumento fica pendurado no último nível gravado — o
 * TIL-05 (Barragem B) passou 9,5h sem publicar exibindo NORMAL verde, e verde afirma
 * "medido e dentro da faixa". A última leitura é um número congelado, não medição.
 *
 * A ação também é outra: telemetria/campo e a `TARP-PS-TILT`, não cobrança de carta
 * (`SEM_FAIXA`) nem conferência de cadastro (`SEM_LEITURA`).
 */
export const SEM_SINAL = 'SEM_SINAL';

/**
 * Instrumento DECLARADO fora de serviço — GUPM-307.
 *
 * Distinto de `SEM_SINAL`, e a diferença é a única coisa que importa aqui: sem sinal é
 * silêncio INFERIDO e pede ação (abre `TARP-PS-*`); fora de serviço é silêncio DECLARADO
 * pelo RT e não pede nada — o instrumento já está sendo tratado. Os dois se apresentam
 * como `isOnline === false`.
 *
 * Por que não deixar o nível medido aparecer: em modo fora de serviço a última leitura é
 * um número congelado de antes da retirada, e o `INA-12A` prova o custo de exibi-lo como
 * atual — 798,58 m contra limiar de Emergência de 795,95, seis dias depois de o
 * instrumento sair de campo, mantendo o botão de emergência do header pulsando em toda
 * tela. O valor e a data continuam visíveis no selo; o que sai é a AFIRMAÇÃO de nível.
 */
export const FORA_DE_SERVICO = 'FORA_DE_SERVICO';

/** Os 4 níveis do TARP mais os estados de apresentação sem lastro. */
export type NivelExibido =
  | TarpLevel
  | typeof SEM_LEITURA
  | typeof SEM_FAIXA
  | typeof SEM_SINAL
  | typeof FORA_DE_SERVICO;

/**
 * Tipos de instrumento para os quais a perda de sinal APARECE na tela nesta rodada.
 *
 * Portão de rollout, não regra física: perder sinal é igualmente relevante para INA/PZ.
 * Generalizar é o GUPM-308, e o gate sai junto.
 *
 * GUPM-307 — o caso que este portão protegia deixou de depender dele: o `INA-12A` está
 * `is_online = false` porque foi **desativado em campo**, e agora isso é declarável
 * (`foraDeServicoDesde`). `semSinalVisivel` já devolve `false` para quem está declarado,
 * então generalizar o portão não passa a acusar equipamento em manutenção.
 *
 * Chave é o enum do BANCO (`TILT`), porque quem pergunta aqui é a UI. Cadência e janela
 * de frescor usam a chave da API (`TIL`) — ver `tipoApiDeInstrumento`.
 */
const PERDA_DE_SINAL_VISIVEL: ReadonlySet<string> = new Set(['TILT']);

/**
 * O instrumento deve ser exibido como sem sinal?
 *
 * `isOnline === false` estrito: `null` é "status nunca derivado" (instrumento recém
 * cadastrado) e **não** é "está mudo". Colapsar os dois faria todo cadastro novo nascer
 * acusado.
 *
 * GUPM-307 — instrumento DECLARADO fora de serviço nunca é "sem sinal": o silêncio dele é
 * conhecido e já está sendo tratado. Sem esta cláusula, consertar o alarme falso de
 * emergência do `INA-12A` só o trocaria por um card de perda de sinal todo dia no mesmo
 * instrumento. `foraDeServicoDesde` é campo OBRIGATÓRIO no parâmetro para o `tsc`
 * enumerar os chamadores — a alternativa é lembrar de dez lugares, e este app já perdeu
 * essa aposta duas vezes (GUPM-270, GUPM-303).
 */
export function semSinalVisivel(
  inst: { type: string; isOnline: boolean | null; foraDeServicoDesde: Date | null },
): boolean {
  if (foraDeServico(inst)) return false;
  return inst.isOnline === false && PERDA_DE_SINAL_VISIVEL.has(inst.type);
}

/**
 * Nível a exibir a partir da leitura mais recente do instrumento.
 *
 * Ausência de leitura (`undefined`/`null`) → `SEM_LEITURA`. Leitura existente
 * **sem** nível gravado é dado incompleto, não ausência de medição: continua
 * caindo em NORMAL, como antes deste card — `readings.level` é NOT NULL no
 * banco, então esse caso só aparece em mock e em leitura vinda de mapper antigo.
 */
export function nivelExibido(
  ultima: { level: TarpLevel | null } | null | undefined,
): NivelExibido {
  if (ultima == null) return SEM_LEITURA;
  return ultima.level ?? 'NORMAL';
}

/**
 * Nível a exibir considerando também a existência de faixa de controle — GUPM-247.
 *
 * É a forma completa de `nivelExibido`: quem tem acesso ao instrumento (e portanto
 * sabe se há carta) deve usar esta. `nivelExibido` fica para os pontos que só têm
 * a leitura em mão.
 *
 * **Precedência: `SEM_LEITURA` antes de `SEM_FAIXA`.** Quando o instrumento está
 * nos dois estados (1 dos 84 no banco real), o fato mais básico é o que não mediu
 * — e a ação que ele pede é outra (telemetria, não cobrança de carta).
 *
 * **Por que ignorar o `level` gravado quando não há faixa:** `ingest-gpx-core`
 * insere `level: 'NORMAL'` provisório e só o troca quando `classify_reading_level`
 * devolve outro valor; sem carta vigente a função devolve `null` e o writer faz
 * `?? 'NORMAL'`. O valor está no banco e **não tem lastro** — repeti-lo na tela
 * seria afirmar "dentro da faixa" sobre uma faixa que não existe.
 *
 * **`semSinal` é OBRIGATÓRIO (GUPM-303)** e não tem default. Motivo: a regra "vence o
 * pior canal com faixa" já foi ligada ponto a ponto neste app e ponto a ponto foi
 * esquecida — primeiro em três lugares, depois em mais dois (ver o TSDoc de
 * `nivelExibidoDeInstrumento`). Parâmetro obrigatório faz o `tsc` enumerar os chamadores
 * e o build quebrar até que cada um passe o fato, em vez de depender de alguém lembrar de
 * oito lugares. Derive-o com `semSinalVisivel(instrumento)`.
 *
 * **Precedência: `SEM_LEITURA` › `SEM_SINAL` › `SEM_FAIXA` › nível medido.** Nunca ter
 * medido é o fato mais básico. Estar mudo vem antes de não ter régua porque régua
 * faltando é condição permanente e ficar mudo é EVENTO — é o evento que pede ação agora.
 */
export function nivelDeInstrumento(
  ultima: { level: TarpLevel | null } | null | undefined,
  temFaixa: boolean,
  semSinal: boolean,
  foraDeServicoDeclarado: boolean,
): NivelExibido {
  if (ultima == null) return SEM_LEITURA;
  if (foraDeServicoDeclarado) return FORA_DE_SERVICO;
  if (semSinal) return SEM_SINAL;
  if (!temFaixa) return SEM_FAIXA;
  return ultima.level ?? 'NORMAL';
}

/**
 * Instrumento tem carta de controle? `thresholdsSource === null` é o sinal
 * canônico desde o GUPM-227 — instrumento sem os 3 pontos do inventário v2 fica
 * SEM carta em vez de ganhar banda vazia (banda toda-nula casa com qualquer
 * leitura e produz falso ALERTA).
 */
export function temFaixaDeControle(inst: {
  thresholdsSource: string | null;
  /** GUPM-270: canais com carta vigente (tiltímetro só tem carta nos eixos). */
  canaisComFaixa?: readonly Canal[];
}): boolean {
  return inst.thresholdsSource !== null || (inst.canaisComFaixa?.length ?? 0) > 0;
}

/** Ordem de gravidade dos 4 níveis medidos. */
const SEVERIDADE: Record<TarpLevel, number> = {
  NORMAL: 0, ATENCAO: 1, ALERTA: 2, EMERGENCIA: 3,
};

/**
 * O mais grave de um conjunto de níveis. Lista vazia → `NORMAL` (não há o que
 * agravar); quem precisa distinguir "vazio" de "tudo normal" checa antes.
 */
export function piorNivel(niveis: readonly TarpLevel[]): TarpLevel {
  return niveis.reduce<TarpLevel>(
    (pior, n) => (SEVERIDADE[n] > SEVERIDADE[pior] ? n : pior),
    'NORMAL',
  );
}

/**
 * Nível de exibição de instrumento MULTI-CANAL — GUPM-270.
 *
 * `nivelDeInstrumento` assume UMA leitura atual. O tiltímetro tem três no mesmo
 * timestamp (eixo A, eixo B, temperatura) e `readings_current` desempatava por
 * `id` — sorteio. O semáforo podia publicar a linha da temperatura: 58 °C existem
 * no histórico, e contra uma régua de 15° isso é emergência.
 *
 * A regra: vence o PIOR canal COM FAIXA. Canal sem carta não classifica e por isso
 * não entra na conta — a temperatura sai por construção, não por lista de exceção,
 * e um canal novo que apareça amanhã sem carta também fica de fora sozinho.
 *
 * Precedência dos estados sem lastro segue a do GUPM-247: `SEM_LEITURA` antes de
 * `SEM_FAIXA`. E "mediu só o canal sem régua" conta como SEM_LEITURA — o que tem
 * régua não foi medido, e é sobre ele que a tela se pronuncia.
 */
export function nivelDeInstrumentoMultiCanal(
  leiturasAtuais: readonly { canal: Canal | null; level: TarpLevel | null }[],
  canaisComFaixa: readonly (Canal | null)[],
  semSinal: boolean,
  foraDeServicoDeclarado: boolean,
): NivelExibido {
  if (leiturasAtuais.length === 0) return SEM_LEITURA;
  // GUPM-307 — declarado fora de serviço vence os canais: os três eixos podem estar todos
  // dentro da faixa e serem todos da mesma leitura de antes da retirada.
  if (foraDeServicoDeclarado) return FORA_DE_SERVICO;
  // GUPM-303 — mudo vence o pior canal: os três eixos podem estar todos dentro da faixa
  // e serem todos da mesma leitura congelada.
  if (semSinal) return SEM_SINAL;
  if (canaisComFaixa.length === 0) return SEM_FAIXA;
  const comFaixa = leiturasAtuais.filter((r) => canaisComFaixa.includes(r.canal));
  if (comFaixa.length === 0) return SEM_LEITURA;
  return piorNivel(comFaixa.map((r) => r.level ?? 'NORMAL'));
}

/**
 * Nível exibido de um instrumento a partir do conjunto de leituras atuais — a forma
 * ÚNICA de responder "que cor este instrumento tem".
 *
 * GUPM-270 — existe porque a regra "vence o pior canal com faixa" foi ligada ponto a
 * ponto, e ponto a ponto ela foi esquecida: primeiro em três lugares, depois em mais
 * dois (o contador do Dashboard, que alimenta o semáforo do Header, e a cor do card
 * "Última leitura"). Enquanto cada chamador reescrever o `if (canais.length > 0)`, o
 * próximo ponto de render nasce errado de novo. Aqui a decisão mora num lugar só:
 * quem precisa do nível de um instrumento chama isto e não tem como divergir.
 *
 * Recebe TODAS as leituras atuais e filtra internamente — é a forma que os chamadores
 * já têm em mão (`readings_current` inteiro + o instrumento).
 */
export function nivelExibidoDeInstrumento(
  instrument: {
    id: string;
    /** GUPM-303 — enum do BANCO (`TILT`), usado pelo portão de rollout da perda de sinal. */
    type: string;
    /** GUPM-303 — `false` = mudo; `null` = status nunca derivado (≠ mudo). */
    isOnline: boolean | null;
    /** GUPM-307 — declaração do RT; vence o nível medido e o sem sinal. */
    foraDeServicoDesde: Date | null;
    thresholdsSource: string | null;
    canaisComFaixa?: readonly Canal[];
  },
  readings: readonly { instrumentId: string; canal: Canal | null; level: TarpLevel | null; ts: Date }[],
): NivelExibido {
  const doInstrumento = readings.filter((r) => r.instrumentId === instrument.id);
  const canais = instrument.canaisComFaixa ?? [];
  const semSinal = semSinalVisivel(instrument);
  const fs = foraDeServico(instrument);
  // Multi-canal tem estados próprios (o canal sem régua não classifica), então a
  // pergunta é outra função — não o `nivelDeInstrumento` de uma leitura só.
  if (canais.length > 0) return nivelDeInstrumentoMultiCanal(doInstrumento, canais, semSinal, fs);
  const latest = [...doInstrumento].sort((a, b) => b.ts.getTime() - a.ts.getTime())[0];
  return nivelDeInstrumento(latest, temFaixaDeControle(instrument), semSinal, fs);
}

/**
 * A leitura que GOVERNA o que a tela mostra sobre um instrumento — GUPM-270.
 *
 * Para instrumento de canal único é a mais recente, como sempre foi. Para
 * multi-canal é a do canal MAIS GRAVE entre os que têm faixa: qualquer ponto de
 * render que precise de uma leitura só (valor exibido, nível do pino, linha da
 * lista de anomalias) tem de usar esta, não `[0]` de um `sort` por `ts`.
 *
 * **Por que existe:** com três canais gravados no MESMO timestamp, ordenar por `ts`
 * não desempata e a escolha sai arbitrária. A linha sorteada podia ser a de
 * `temperatura`, cujo `level` é o `'NORMAL'` provisório que o writer grava quando
 * não há carta — verde afirmando "dentro da faixa" sobre um eixo que estourou. É a
 * mesma classe de falsidade do GUPM-228 e do GUPM-247, e reapareceu em três pontos
 * de render depois que a `readings_current` passou a devolver uma linha por canal.
 *
 * `undefined` quando não há leitura em canal com faixa — o chamador decide se isso
 * é `SEM_LEITURA` (use `nivelDeInstrumentoMultiCanal`) ou apenas "sem valor a exibir".
 */
export function leituraGovernante<
  R extends { canal: Canal | null; level: string | null; ts: Date },
>(
  leiturasDoInstrumento: readonly R[],
  canaisComFaixa: readonly Canal[],
): R | undefined {
  const maisRecente = (rs: readonly R[]): R | undefined =>
    [...rs].sort((a, b) => b.ts.getTime() - a.ts.getTime())[0];

  if (canaisComFaixa.length === 0) return maisRecente(leiturasDoInstrumento);

  const comFaixa = leiturasDoInstrumento.filter(
    (r) => r.canal !== null && canaisComFaixa.includes(r.canal),
  );
  if (comFaixa.length === 0) return undefined;
  // `level` chega como `string` de alguns chamadores (o painel do Resumo Técnico):
  // o que não for um dos 4 níveis conta como NORMAL, mesma normalização que eles já
  // faziam. Aceitar `string` aqui evita obrigar cada chamador a estreitar o tipo
  // antes de perguntar "qual leitura manda?".
  const nivel = (r: R): TarpLevel =>
    (r.level && r.level in SEVERIDADE ? (r.level as TarpLevel) : 'NORMAL');
  const pior = piorNivel(comFaixa.map(nivel));
  // Entre canais empatados no pior nível, a leitura mais recente.
  return maisRecente(comFaixa.filter((r) => nivel(r) === pior));
}

/**
 * TODOS os estados de exibição, na ordem em que a UI os apresenta: os 4 níveis medidos,
 * depois o sem sinal (pede ação), depois os dois frios (condição de carta/cadastro).
 *
 * Fonte única. Existia copiada em dois lugares — `NIVEL_EXIBIDO_ORDER` (legenda e filtro
 * do mapa) e `STATUS_FILTRAVEIS` (dropdown do Resumo Técnico) — e a segunda ficou para
 * trás quando o `SEM_SINAL` entrou no GUPM-303: a linha aparecia na tabela, pintada e
 * clicável, e o RT não conseguia filtrar por ela.
 *
 * O `tsc` não pega isso: `NivelExibido[]` aceita subconjunto, então uma lista incompleta
 * compila. A defesa é este array ser o único, mais o teste que exige que ele cubra todas
 * as chaves de `NIVEL_EXIBIDO_UI`.
 */
export const NIVEIS_EXIBIDOS: readonly NivelExibido[] = [
  'NORMAL', 'ATENCAO', 'ALERTA', 'EMERGENCIA', SEM_SINAL, FORA_DE_SERVICO, SEM_FAIXA,
  SEM_LEITURA,
];

/**
 * Narrowing: o nível é um dos 4 realmente MEDIDOS contra faixa?
 *
 * Serve aos pontos que precisam de um `TarpLevel` de verdade — ícone de
 * severidade, destaque de anomalia, ordenação por gravidade. Concentra a regra
 * num lugar: antes, cada ponto de render repetia `!== SEM_LEITURA`, e ao entrar
 * o segundo estado sem lastro todos teriam de ser caçados de novo (o compilador
 * pegou os 3, mas só porque o tipo é estreito — não conte com isso sempre).
 */
export function ehNivelMedido(nivel: NivelExibido): nivel is TarpLevel {
  // GUPM-303: `SEM_SINAL` entrou aqui à mão. Este guard é um `!==` encadeado — a união
  // estreita protege quem faz `switch`, mas quem só chama isto recebe o estado novo
  // classificado como "medido" e o compilador não reclama. Estado novo na união ⇒
  // revisitar esta linha.
  // GUPM-307: `FORA_DE_SERVICO` entrou nesta mesma linha, pelo mesmo motivo.
  return nivel !== SEM_LEITURA && nivel !== SEM_FAIXA && nivel !== SEM_SINAL
    && nivel !== FORA_DE_SERVICO;
}

/**
 * A linha deve OFERECER a TARP (chip clicável, fundo preenchido)?
 *
 * Existe separada de `ehNivelMedido` porque as duas perguntas divergiram no GUPM-303.
 * `ehNivelMedido` responde "há medição contra faixa?" — e a resposta para o sem sinal é
 * **não**, o que está certo e precisa continuar assim: é ela que impede o "sem faixa" de
 * virar convite a abrir emergência (GUPM-247). Esta responde "há ação a tomar daqui?", e
 * aí o sem sinal entra: ele tem protocolo próprio no catálogo (`TARP-PS-TILT`, ALERTA),
 * enquanto `SEM_FAIXA` pede carta e `SEM_LEITURA` pede conferência de cadastro — nenhum
 * dos dois tem TARP.
 *
 * As tabelas do Resumo Técnico usavam `ehNivelMedido` para as DUAS perguntas, e por isso
 * a linha do tiltímetro mudo saía com o chip vazado: o card promete "oferece a TARP
 * certa" e ali não oferecia.
 */
export function ehAnomaliaAcionavel(nivel: NivelExibido): boolean {
  // GUPM-307 — fora de serviço é o oposto do sem sinal aqui: já está sendo tratado fora do
  // app, e oferecer TARP convidaria a abrir plano de ação para trabalho em curso.
  if (nivel === FORA_DE_SERVICO) return false;
  if (nivel === SEM_SINAL) return true;
  return ehNivelMedido(nivel) && nivel !== 'NORMAL';
}
